import { z } from 'zod';

// Schema booking pubblico — fonte unica di verità (Task #1222).
// I requisiti seguono fedelmente la versione locale storica del widget per
// evitare regressioni: `serviceId` obbligatorio, `consultantId` stringa
// libera (non uuid: alcuni form usano slug), customerName/Email opzionali
// (alcuni form raccolgono dati via customFields), `privacyAccepted` bool
// opzionale (il check di policy è applicativo e dipende dal documento
// attivo del tenant). Schema permissivo (no `.strict()`) per accettare
// payload widget legacy.
// Task #3615 — vincoli stretti post-osservazione, MOLTO conservativi perché
// questi form sono compilati da anonimi (booking pubblico): aggiunti solo
// limiti di lunghezza generosi sui pochi campi ancora illimitati. Nessuna
// regola di formato aggiunta su `start`/`consultantId` (slug o ISO con/senza
// ora) e nessun cambio ai `min`/`.strict()` esistenti.
export const bookAppointmentBodySchema = z.object({
  serviceId: z.string().uuid(),
  productId: z.string().uuid().optional(),
  // Task #2752: sede scelta dal cliente quando il form abilita allow_store_choice.
  storeId: z.string().uuid().optional(),
  // slug o uuid: nessun formato imposto, solo tetto di lunghezza.
  consultantId: z.string().min(1).max(255),
  // date-string via new Date(...): max senza regex (rule 7).
  start: z.string().min(8, 'Data/ora di inizio obbligatoria').max(64),
  customerName: z.string().max(255).optional(),
  customerEmail: z.string().email('Formato email non valido').max(255).optional(),
  customerPhone: z.string().max(50).optional(),
  notes: z.string().max(2000).optional(),
  privacyAccepted: z.boolean().optional(),
  privacyDocumentId: z.string().uuid().optional(),
  privacyDocumentVersion: z.number().int().positive().optional(),
  marketingAccepted: z.boolean().optional(),
  gdprConsents: z.record(z.boolean()).optional(),
  customFields: z.record(z.unknown()).optional(),
  utm: z.record(z.unknown()).optional(),
  // I token captcha (Turnstile/reCAPTCHA) possono essere lunghi: tetto ampio.
  captchaToken: z.string().max(10000).optional(),
});
export type BookAppointmentBody = z.infer<typeof bookAppointmentBodySchema>;

// Server-to-server: stesso contratto del widget, ma nessun campo extra
// (in particolare bypassCaptcha rimane una decisione esclusivamente server).
export const submitAppointmentFormBodySchema = bookAppointmentBodySchema.strict();
export type SubmitAppointmentFormBody = z.infer<typeof submitAppointmentFormBodySchema>;

export const rescheduleAppointmentBodySchema = z.object({
  // date-string via new Date(...): max senza regex (rule 7).
  start: z.string().min(8, 'Data/ora di inizio obbligatoria').max(64),
}).passthrough();
export type RescheduleAppointmentBody = z.infer<typeof rescheduleAppointmentBodySchema>;

export const cancelTokenParamsSchema = z.object({
  cancelToken: z.string().min(8).max(512),
});

/** Serialized snapshot exposed by the existing public token endpoint. */
export const publicBookingSnapshotSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }),
  status: z.string(),
  type_name: z.string().nullable(),
  customer_name: z.string().nullable(),
  customer_email: z.string().nullable(),
  form_name: z.string().nullable(),
  branding: z.object({
    primaryColor: z.string().optional(),
    accentColor: z.string().optional(),
    logoUrl: z.string().optional(),
    fontFamily: z.string().optional(),
    defaultTheme: z.enum(['light', 'dark']).optional(),
    primary_color: z.string().optional(),
    accent_color: z.string().optional(),
    logo_url: z.string().optional(),
    font_family: z.string().optional(),
    default_theme: z.enum(['light', 'dark']).optional(),
  }).passthrough().nullable(),
  cancel_deadline_hours: z.number().nonnegative().nullable(),
  reschedule_deadline_hours: z.number().nonnegative().nullable(),
});
export type PublicBookingSnapshot = z.infer<typeof publicBookingSnapshotSchema>;

// Form configuration wire DTOs. UI state deliberately remains camelCase and
// is mapped separately; no booking-window settings are stored on a form.
export const appointmentFormCustomFieldSchema = z.object({
  id: z.string(), label: z.string(),
  type: z.enum(['text', 'email', 'phone', 'textarea', 'select', 'checkbox']),
  required: z.boolean(), options: z.array(z.string()).optional(),
  placeholder: z.string().optional(),
}).passthrough();
export const appointmentFormBrandingSchema = publicBookingSnapshotSchema.shape.branding.unwrap();
export const appointmentFormBodySchema = z.object({
  slug: z.string().min(3).max(120).optional(),
  name: z.string().min(1).max(255),
  store_id: z.string().uuid().optional(),
  description: z.string().nullable().optional(),
  widget_style: z.enum(['calcom', 'doctolib']).optional(),
  branding: z.record(z.unknown()).optional(),
  consultant_mode: z.enum(['all', 'whitelist']).optional(),
  consultant_filter_mode: z.preprocess(v => v === 'skill' ? 'skill_shifts' : v,
    z.enum(['any', 'skill_shifts', 'manual'])).optional(),
  consultant_skill_filter: z.boolean().optional(),
  allow_consultant_choice: z.boolean().optional(),
  allow_any_consultant: z.boolean().optional(),
  allow_store_choice: z.boolean().optional(),
  service_mode: z.enum(['all', 'whitelist']).optional(),
  service_ids: z.array(z.string().uuid()).optional(),
  consultant_ids: z.array(z.string().uuid()).optional(),
  product_ids: z.array(z.string().uuid()).optional(),
  service_selection_mode: z.enum(['auto', 'always', 'required', 'never']).optional(),
  auto_confirm: z.boolean().optional(),
  require_customer_name: z.boolean().optional(),
  require_customer_email: z.boolean().optional(),
  require_customer_phone: z.boolean().optional(),
  // Historical API accepts opaque JSON entries. Keep the wire lossless; UI
  // consumers narrow each entry separately rather than rejecting a whole form.
  custom_fields: z.array(z.unknown()).optional(),
  privacy_text: z.string().nullable().optional(),
  privacy_url: z.string().nullable().optional(),
  marketing_opt_in: z.boolean().optional(),
  show_price: z.boolean().optional(),
  confirmation_message: z.string().nullable().optional(),
  redirect_url: z.string().nullable().optional(),
  send_customer_email: z.boolean().optional(),
  send_consultant_email: z.boolean().optional(),
  attach_ics: z.boolean().optional(),
  honeypot_field_name: z.string().nullable().optional(),
  captcha_provider: z.enum(['hcaptcha', 'recaptcha']).nullable().optional(),
  captcha_site_key: z.string().nullable().optional(),
  captcha_secret_key: z.string().nullable().optional(),
  cancel_deadline_hours: z.number().int().min(0).max(720).optional(),
  reschedule_deadline_hours: z.number().int().min(0).max(720).optional(),
  is_active: z.boolean().optional(),
});
export type AppointmentFormBody = z.infer<typeof appointmentFormBodySchema>;

export const appointmentFormDetailSchema = appointmentFormBodySchema.omit({
  captcha_secret_key: true, service_ids: true, consultant_ids: true, product_ids: true,
}).extend({
  id: z.string(), slug: z.string(),
  store_id: z.string().nullable(),
  branding: appointmentFormBrandingSchema.nullable().optional(),
  custom_fields: z.array(z.unknown()).nullable().optional(),
  cancel_deadline_hours: z.number().nonnegative().nullable().optional(),
  reschedule_deadline_hours: z.number().nonnegative().nullable().optional(),
  captcha_secret_configured: z.boolean().optional(),
  created_at: z.union([z.date(), z.string()]).transform(value => value instanceof Date ? value.toISOString() : value).optional(),
  updated_at: z.union([z.date(), z.string()]).transform(value => value instanceof Date ? value.toISOString() : value).optional(),
  services: z.array(z.object({ service_type_id: z.string() })).default([]),
  consultants: z.array(z.object({ user_id: z.string() })).default([]),
  products: z.array(z.object({ product_id: z.string() })).default([]),
}).passthrough();
export type AppointmentFormDetail = z.infer<typeof appointmentFormDetailSchema>;

const appointmentFormStoreSchema = z.object({
  id: z.string(), name: z.string(), city: z.string().nullable().optional(),
});
export const publicAppointmentFormSchema = appointmentFormDetailSchema.omit({
  store_id: true, services: true, consultants: true, products: true,
}).extend({
  branding: appointmentFormBrandingSchema.nullable(),
  privacy_text: z.string().nullable(),
  privacy_url: z.string().nullable(),
  confirmation_message: z.string().nullable(),
  redirect_url: z.string().nullable(),
  honeypot_field_name: z.string().nullable(),
  captcha_provider: z.enum(['hcaptcha', 'recaptcha']).nullable(),
  captcha_site_key: z.string().nullable(),
  stores: z.array(appointmentFormStoreSchema).optional(),
  default_store: appointmentFormStoreSchema.nullable().optional(),
  gdpr_document: z.object({
    id: z.string(), title: z.string(), content: z.string(), version: z.number(),
  }).nullable().optional(),
  gdpr_consents: z.array(z.object({
    key: z.string(), label: z.string(), required: z.boolean(), description: z.string().optional(),
  })).optional(),
  // Projections of canonical calendar_settings: minutes and days respectively.
  min_advance_booking: z.number().nonnegative().nullable().optional(),
  max_advance_booking: z.number().nonnegative().nullable().optional(),
});
export type PublicAppointmentForm = z.infer<typeof publicAppointmentFormSchema>;
